import { SimulationConfig } from '../../types';
import { BALL_VERTEX_SHADER, BALL_FRAGMENT_SHADER } from '../../shaders';
import { WebGLContext, MAX_BALLS } from './WebGLContext';
import { WebGLUtils } from './WebGLUtils';

export class TrailRenderer {
  private program: WebGLProgram;
  private vao: WebGLVertexArrayObject;
  private posBuffer: WebGLBuffer;
  private radiusBuffer: WebGLBuffer;
  private colorBuffer: WebGLBuffer;
  private alphaBuffer: WebGLBuffer;
  private posData: Float32Array;
  private radiusData: Float32Array;
  private colorData: Float32Array;
  private alphaData: Float32Array;

  constructor(private context: WebGLContext) {
    const gl = context.gl;
    this.program = WebGLUtils.createProgram(gl, BALL_VERTEX_SHADER, BALL_FRAGMENT_SHADER);
    this.posData = new Float32Array(MAX_BALLS * 2);
    this.radiusData = new Float32Array(MAX_BALLS);
    this.colorData = new Float32Array(MAX_BALLS * 3);
    this.alphaData = new Float32Array(MAX_BALLS);

    this.vao = gl.createVertexArray()!;
    gl.bindVertexArray(this.vao);

    gl.bindBuffer(gl.ARRAY_BUFFER, context.geoBuffer);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);

    this.posBuffer = context.createAttribBuffer(1, 2, this.posData.byteLength);
    this.radiusBuffer = context.createAttribBuffer(2, 1, this.radiusData.byteLength);
    this.colorBuffer = context.createAttribBuffer(3, 3, this.colorData.byteLength);
    this.alphaBuffer = context.createAttribBuffer(6, 1, this.alphaData.byteLength);

    gl.bindVertexArray(null);
  }

  render(trailCircles: any[], config: SimulationConfig, camera: { x: number, y: number, scale: number }, zoomLevel: number = 1.0) {
      if (trailCircles.length === 0) return;
      const gl = this.context.gl;
      gl.useProgram(this.program);
      gl.bindVertexArray(this.vao);

      WebGLUtils.updateCommonUniforms(gl, this.program, this.context.width, this.context.height, camera, zoomLevel);
      gl.uniform1f(gl.getUniformLocation(this.program, 'u_time'), Date.now());
      this.updateUniforms(config);

      const count = Math.min(trailCircles.length, MAX_BALLS);
      const startIdx = Math.max(0, trailCircles.length - MAX_BALLS);
      for (let i = 0; i < count; i++) {
          const tc = trailCircles[startIdx + i];
          if (!tc) continue;
          
          const pr = trailCircles.length > 1 ? (startIdx + i) / (trailCircles.length - 1) : 1;
          const scale = config.trailMatchBallSize ? 1.0 : (config.trailEndScale + (config.trailStartScale - config.trailEndScale) * pr);
          const finalRadius = tc.radius * scale * (config.trailWidthMultiplier || 1.0);

          this.posData[i * 2] = tc.pos.x;
          this.posData[i * 2 + 1] = tc.pos.y;
          this.radiusData[i] = finalRadius;
          WebGLUtils.parseHsl(tc.hue, this.colorData, i * 3);
          this.alphaData[i] = 1.0; 
      }

      this.uploadData(count);
      gl.drawArraysInstanced(gl.TRIANGLE_STRIP, 0, 4, count);
      gl.bindVertexArray(null);
  }

  private updateUniforms(config: SimulationConfig) {
    const gl = this.context.gl;
    gl.uniform1f(gl.getUniformLocation(this.program, 'u_pulseSpeed'), 0);
    gl.uniform1f(gl.getUniformLocation(this.program, 'u_squashFactor'), 0);

    const showBorder = !config.trailPersistence || config.trailApplyBorderToPersistence;
    gl.uniform1f(gl.getUniformLocation(this.program, 'i_borderWidth'), showBorder ? (config.trailStrokeWidth || 0) : 0);
    
    const tCol = WebGLUtils.hexToRgb(config.trailStrokeColor || '#000000');
    gl.uniform3f(gl.getUniformLocation(this.program, 'u_borderColor'), tCol[0]/255, tCol[1]/255, tCol[2]/255);
    gl.uniform1f(gl.getUniformLocation(this.program, 'u_strokeOpacity'), config.trailStrokeOpacity !== undefined ? config.trailStrokeOpacity : 1.0);

    const styleMap: Record<string, number> = { 'SHINY': 0, 'NEON': 1, 'CHROME': 2, 'MATTE': 3, 'GHOST': 4 };
    gl.uniform1i(gl.getUniformLocation(this.program, 'u_ballStyle'), styleMap[config.ballStyle] || 0);

    gl.uniform1f(gl.getUniformLocation(this.program, 'u_glowAmount'), config.ballGlowRadius || 0);
    const gCol = WebGLUtils.hexToRgb(config.ballGlowColor || '#ffffff');
    gl.uniform3f(gl.getUniformLocation(this.program, 'u_glowColor'), gCol[0]/255, gCol[1]/255, gCol[2]/255);
    gl.uniform1f(gl.getUniformLocation(this.program, 'u_glowOpacity'), config.ballGlowOpacity !== undefined ? config.ballGlowOpacity : 1.0);
    gl.uniform1f(gl.getUniformLocation(this.program, 'u_fillOpacity'), config.trailOpacity !== undefined ? config.trailOpacity : 1.0);
  }

  private uploadData(count: number) {
    const gl = this.context.gl;
    gl.bindBuffer(gl.ARRAY_BUFFER, this.posBuffer);
    gl.bufferSubData(gl.ARRAY_BUFFER, 0, this.posData.subarray(0, count * 2));
    gl.bindBuffer(gl.ARRAY_BUFFER, this.radiusBuffer);
    gl.bufferSubData(gl.ARRAY_BUFFER, 0, this.radiusData.subarray(0, count));
    gl.bindBuffer(gl.ARRAY_BUFFER, this.colorBuffer);
    gl.bufferSubData(gl.ARRAY_BUFFER, 0, this.colorData.subarray(0, count * 3));
    gl.bindBuffer(gl.ARRAY_BUFFER, this.alphaBuffer);
    gl.bufferSubData(gl.ARRAY_BUFFER, 0, this.alphaData.subarray(0, count));
  }
}
